import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Leaf, Sparkles, CalendarDays, MapPin } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { LOGO_URL, STOCK_IMAGES } from "@/lib/brand";

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [past, setPast] = useState([]);

  useEffect(() => {
    api.get("/site-settings").then((r) => setSettings(r.data));
    api.get("/events?past=false").then((r) => setFeatured(r.data[0]));
    api.get("/events?past=true").then((r) => setPast(r.data.slice(0, 3)));
  }, []);

  const pastPhotos = past.flatMap((ev) => {
    const shots = (ev.gallery && ev.gallery.length ? ev.gallery : (ev.cover_image ? [ev.cover_image] : []));
    return shots.map((src) => ({ src, title: ev.title, id: ev.id }));
  }).slice(0, 8);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden hero-wash" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={fileUrl(settings?.hero_image) || STOCK_IMAGES.hero}
            alt="Furshala hero"
            className="w-full h-full object-cover opacity-90"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-cream via-brand-cream/85 to-brand-cream/30 md:to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-8 pb-10 sm:pt-24 sm:pb-36 grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-10 items-center">
          <div className="md:col-span-7 space-y-5 sm:space-y-7 animate-fade-up">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Furshala" className="w-16 h-16 sm:w-24 sm:h-24 object-contain" />
              <div>
                <div className="eyebrow">Natural · Wellness · Love</div>
                <div className="font-serif italic text-xl sm:text-2xl text-brand-maroon mt-1">Furshala</div>
              </div>
            </div>
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-brand-soft">
              A community
              <span className="block text-brand-maroon italic">for pets and their humans.</span>
            </h1>
            <p className="text-sm sm:text-lg text-brand-soft/75 max-w-xl leading-relaxed">
              Curated meetups. Handcrafted naturals. Slow afternoons where tails do the talking.
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/events" className="btn-primary-furshala inline-flex items-center gap-2" data-testid="hero-cta-events">
                Explore Events <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/shop" className="btn-outline-furshala" data-testid="hero-cta-shop">
                Browse Shop
              </Link>
            </div>
          </div>

          {/* Featured Event card */}
          {featured && (
            <div className="md:col-span-5 animate-fade-up delay-200" data-testid="featured-event-card">
              <Link to={`/events/${featured.id}`} className="block group">
                <div className="bg-white rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(122,42,45,0.25)] border border-brand-maroon/10">
                  <div className="relative aspect-video sm:aspect-[4/3] overflow-hidden">
                    {featured.cover_image && (
                      <img src={fileUrl(featured.cover_image)} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    )}
                    <div className="absolute top-4 left-4 bg-brand-saffron text-brand-soft text-xs px-3 py-1 rounded-full uppercase tracking-widest font-semibold shadow-sm">
                      Upcoming
                    </div>
                  </div>
                  <div className="p-5 sm:p-7 space-y-2 sm:space-y-3">
                    <h3 className="font-serif text-2xl sm:text-3xl text-brand-soft leading-tight">{featured.title}</h3>
                    <div className="flex flex-wrap gap-4 text-sm text-brand-soft/70">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="w-4 h-4 text-brand-terracotta" /> {featured.date} {featured.time ? `· ${featured.time}` : ""}</span>
                      <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-brand-terracotta" /> {featured.location}</span>
                    </div>
                    <p className="text-sm text-brand-soft/70 leading-relaxed">{featured.description}</p>
                    {featured.seats_note && (
                      <div className="inline-block text-xs text-brand-maroon font-semibold bg-brand-mustard/20 px-3 py-1 rounded-full">{featured.seats_note}</div>
                    )}
                    <div className="pt-2 inline-flex items-center gap-2 text-brand-maroon font-medium group-hover:gap-3 transition-all">
                      Book Now <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Memories strip - shown right below hero so it's visible on mobile with minimal scroll */}
      {pastPhotos.length > 0 && (
        <section className="bg-brand-ivory border-y border-brand-maroon/10" data-testid="home-memories-strip">
          <div className="max-w-7xl mx-auto px-6 sm:px-12 py-10 sm:py-14">
            <div className="flex items-end justify-between mb-5 sm:mb-7">
              <div>
                <div className="eyebrow mb-2">Moments</div>
                <h2 className="font-serif text-2xl sm:text-4xl text-brand-soft leading-tight">From past pawties.</h2>
              </div>
              <Link to="/events" className="text-xs sm:text-sm text-brand-maroon font-medium inline-flex items-center gap-1 hover:gap-2 transition-all whitespace-nowrap" data-testid="memories-see-all">
                See all <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            {/* Mobile: horizontal scroll strip. Desktop: grid */}
            <div className="flex sm:grid sm:grid-cols-4 gap-3 sm:gap-4 overflow-x-auto snap-x snap-mandatory sm:overflow-visible -mx-6 px-6 sm:mx-0 sm:px-0 pb-2 sm:pb-0">
              {pastPhotos.map((p, i) => (
                <Link
                  key={`${p.id}-${i}`}
                  to="/events"
                  className="relative group flex-shrink-0 w-[70%] sm:w-auto snap-start rounded-2xl overflow-hidden border border-brand-maroon/10 bg-white"
                  data-testid={`memory-photo-${i}`}
                >
                  <div className="aspect-[4/5] sm:aspect-square overflow-hidden">
                    <img src={fileUrl(p.src)} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-soft/85 to-transparent p-3">
                    <div className="text-xs sm:text-sm text-brand-cream font-medium truncate">{p.title}</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick links */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-16 sm:py-24" data-testid="quick-links">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {[
            { to: "/events", label: "Events", icon: CalendarDays, sub: "Pawty in the Park", accent: "bg-brand-mustard/20 text-brand-maroon" },
            { to: "/shop", label: "Shop", icon: Sparkles, sub: "Naturally crafted", accent: "bg-brand-peach/30 text-brand-terracotta" },
            { to: "/community", label: "Community", icon: Heart, sub: "Telegram · WhatsApp", accent: "bg-brand-terracotta/20 text-brand-terracotta" },
            { to: "/how-its-made", label: "How It's Made", icon: Leaf, sub: "Our story", accent: "bg-brand-olive/20 text-brand-olive" },
          ].map((q) => {
            const Icon = q.icon;
            return (
              <Link key={q.to} to={q.to} className="group bg-white rounded-2xl p-6 border border-brand-maroon/10 hover:border-brand-maroon/30 transition-all hover:-translate-y-1 hover:shadow-md" data-testid={`quick-link-${q.label.toLowerCase().replace(/[^a-z]/g,"-")}`}>
                <div className={`w-11 h-11 rounded-xl ${q.accent} flex items-center justify-center mb-3`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="font-serif text-xl sm:text-2xl text-brand-soft">{q.label}</div>
                <div className="text-xs text-brand-soft/60 mt-1">{q.sub}</div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Brand values */}
      <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { t: "Naturally sourced", d: "Cotton, hemp, plant dyes. Nothing your dog wouldn't sniff twice.", color: "bg-brand-olive" },
            { t: "Handcrafted, slow", d: "Small batches stitched with care. We choose details over scale.", color: "bg-brand-terracotta" },
            { t: "A real community", d: "We host. You show up with leashes, treats and stories.", color: "bg-brand-mustard" },
          ].map((v) => (
            <div key={v.t} className="space-y-3">
              <div className={`w-10 h-1.5 ${v.color} rounded-full`} />
              <h4 className="font-serif text-2xl text-brand-soft">{v.t}</h4>
              <p className="text-sm text-brand-soft/70 leading-relaxed">{v.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
